import type { LoginResponse } from '~/server/types';
import { jwtDecode } from 'jwt-decode';
import { DealerContentStorageKey } from './useDealerContent';
import { PUBLIC_ROUTES } from '~/middleware/01.auth.global';

interface HumanName {
  firstName: string;
  lastName: string;
  initials: string;
}

export interface Dealer {
  name: string;
  userId: string;
  organizationId: string;
  role: string;
  isPrimary: boolean;
  catalogues: any[]; // You might want to define a more specific type for catalogues
  permissions: string[];
  denyArenaLogo?: boolean;
  denyEclipseLogo?: boolean;
  denyListProductClassifications?: [string];
  dealerContentKey?: string;
  salesManagerInfo?: {
    name: string;
    email: string;
    phoneNumber: string;
    avatarUrl: string;
  };
  customerName: string;
  customerNumber: string;
  creditLimit: string;
  paymentTerms: string;
  transportZone: string;
  customerGroup: string;
  status: string;
}

interface UserClaims {
  'iss': string;
  'exp': number;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
  'id': string;
  'externalId': string | null;
  'name': string;
  'humanName': HumanName;
  'email': string;
  'dealers': Dealer[];
  'iat': number;
  'nbf': number;
}

export const useAuth = () => {
  const user = useState<UserClaims | null>('user', () => null);

  const monitorSession = () => {
    const checkSession = () => {
      const token = getToken();
      const currentRoute = useRoute().path;

      if (!token) {
        const isPublicPath = PUBLIC_ROUTES.includes(currentRoute);
        if (isPublicPath) {
          return;
        }
        logout();
        return;
      }

      try {
        const decoded = jwtDecode(token);
        if (decoded && typeof decoded === 'object' && 'exp' in decoded) {
          const expirationTime = (decoded.exp as number) * 1000; // Convert to milliseconds
          if (Date.now() >= expirationTime) {
            logout();
          }
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
      }
    };

    let interval: NodeJS.Timeout;

    onMounted(() => {
      // Check session every minute
      interval = setInterval(checkSession, 60000);
      checkSession(); // Initial check
    });

    onUnmounted(() => {
      if (interval) {
        clearInterval(interval);
      }
    });
  };

  const setCookieToken = (data: object) => {
    const cookie = useCookie('auth/user', {
      expires: new Date(Date.now() + 8 * 60 * 60 * 1000) // in 8 hours
    });
    // json stringify with some encryption
    const encrypted = JSON.stringify(data);
    cookie.value = encrypted;
  };

  const decodeTokenAndSetState = (data: { token: string }) => {
    const transformToLowerCase = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(item => transformToLowerCase(item));
      }

      if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj).reduce(
          (acc, [key, value]) => {
            const lowercaseKey = key.charAt(0).toLowerCase() + key.slice(1);
            acc[lowercaseKey] = transformToLowerCase(value);
            return acc;
          },
          {} as Record<string, any>
        );
      }

      return obj;
    };

    const cleanupClaims = (decodedToken: any) => {
      const cleanedClaims: Record<string, any> = {};

      Object.entries(decodedToken).forEach(([key, value]) => {
        if (key.includes('userdata')) {
          const jsonString = value as string;
          const userData = JSON.parse(jsonString.replace(/\\/g, ''));
          const transformedUserData = transformToLowerCase(userData);
          Object.assign(cleanedClaims, transformedUserData);
        } else {
          cleanedClaims[key] = value;
        }
      });

      return cleanedClaims as UserClaims;
    };

    const { token } = data;

    if (!token) {
      user.value = null;
      return;
    }

    const decoded = cleanupClaims(jwtDecode(token));

    if (decoded) {
      user.value = decoded;
    }
  };

  const userData = computed(() => {
    if (isAuthenticated.value && !user.value) {
      const token = getToken();
      decodeTokenAndSetState({ token });
    }
    return user.value;
  });

  const getToken = () => {
    const cookie = useCookie<LoginResponse>('auth/user');
    return cookie.value?.token;
  };

  const isAuthenticated = computed(() => {
    const cookie = useCookie('auth/user');
    return !!cookie.value;
  });

  const logout = async () => {
    const cookie = useCookie('auth/user');
    cookie.value = null;
    user.value = null;

    await navigateTo('/login');

    // Clear dealer content from session storage
    if (import.meta.client) {
      sessionStorage.removeItem(DealerContentStorageKey);
      // Remove no-scroll class when logging out
      document.body.classList.remove('no-scroll');
      document.body.style.overflowY = 'auto';
    }
  };

  const login = async (credentials: { email: string; password: string }) => {
    try {
      const { data, error } = await useFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      });

      if (data.value) {
        setCookieToken(data.value);
        decodeTokenAndSetState(data.value);
        return { success: true };
      } else if (error.value) {
        const errorMessage = error.value.data?.data?.errorMessage || 'An unknown error occurred.';
        return {
          success: false,
          message: errorMessage
        };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error during login:', error);
      return {
        success: false,
        message: (error as Error).message || 'An unknown error occurred.'
      };
    }
  };

  /**
   * Request a password reset
   * @param {string} email
   */
  const requestPasswordReset = async (email: string, templateName: string): Promise<{ success: boolean }> => {
    const { error } = await useFetch('/api/auth/request-reset', {
      method: 'POST',
      body: JSON.stringify({
        email,
        templateName
      })
    });

    if (error.value) {
      console.error('Request failed with error: ' + error.value);
      return { success: false };
    }

    return { success: true };
  };

  /**
   * Submit a password reset
   * @param {string} email
   * @param {string} token
   * @param {string} password
   */
  const resetPassword = async (email: string, token: string, password: string): Promise<{ success: boolean }> => {
    // Replace spaces with correct symbols
    const cleanToken = token?.replace(/ /g, '+');

    const { error } = await useFetch('/api/auth/reset', {
      method: 'POST',
      body: {
        email,
        token: cleanToken,
        password
      }
    });

    if (error.value) {
      console.error('Request failed with error: ' + error.value);
      return { success: false };
    }

    return { success: true };
  };

  return {
    userData,
    getToken,
    isAuthenticated,
    logout,
    login,
    decodeTokenAndSetState,
    requestPasswordReset,
    resetPassword,
    monitorSession
  };
};
